import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, CornerDownLeft } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { getNav, filtrarPorPermissoes } from './Sidebar'

interface MenuEntry { to: string; label: string; grupo?: string }

const norm = (s: string) =>
  (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

// Busca de menus no cabeçalho ("command palette"): digita o nome de qualquer
// opção do menu (ex.: "acerto de cargas", "filiais") e navega pra ela.
// Respeita módulos ativos + permissões do perfil (não lista o que o user não vê).
export default function GlobalMenuSearch() {
  const { user, empresa } = useAuth()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isSuperAdmin = user?.perfil === 'super_admin'
  const isAdmin = isSuperAdmin || user?.nivel === 'admin' || user?.perfil === 'admin'

  // Catálogo plano de telas a partir do menu real do usuário
  const catalogo = useMemo<MenuEntry[]>(() => {
    const nav = getNav(isSuperAdmin, empresa?.modulos_ativos ?? null, 0, empresa?.id ?? null, 0, 0)
    const visivel = nav.filter(it => (!it.adminOnly || isAdmin) && (!it.superOnly || isSuperAdmin))
    const filtrado = filtrarPorPermissoes(visivel, user?.permissoes, isAdmin)
    const out: MenuEntry[] = []
    const seen = new Set<string>()
    for (const it of filtrado) {
      if (it.children?.length) {
        for (const ch of it.children) {
          const key = ch.to + '|' + ch.label
          if (seen.has(key)) continue
          seen.add(key)
          out.push({ to: ch.to, label: ch.label, grupo: it.label })
        }
      } else {
        if (seen.has(it.to)) continue
        seen.add(it.to)
        out.push({ to: it.to, label: it.label })
      }
    }
    return out
  }, [isSuperAdmin, isAdmin, empresa?.id, empresa?.modulos_ativos, user?.permissoes])

  const resultados = useMemo<MenuEntry[]>(() => {
    const nq = norm(q)
    if (!nq) return []
    const termos = nq.split(/\s+/).filter(Boolean)
    const scored = catalogo
      .map(e => {
        const alvo = norm(`${e.grupo || ''} ${e.label}`)
        const labelN = norm(e.label)
        const casaTodos = termos.every(t => alvo.includes(t))
        if (!casaTodos) return null
        // Ranking: label começa com a query > label contém > só grupo contém
        let score = 3
        if (labelN.startsWith(nq)) score = 0
        else if (labelN.includes(nq)) score = 1
        else if (termos.every(t => labelN.includes(t))) score = 2
        return { e, score }
      })
      .filter(Boolean) as { e: MenuEntry; score: number }[]
    scored.sort((a, b) => a.score - b.score || a.e.label.localeCompare(b.e.label))
    return scored.slice(0, 8).map(s => s.e)
  }, [q, catalogo])

  useEffect(() => { setActive(0) }, [q])

  // Atalho Ctrl/Cmd+K foca a busca
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault(); inputRef.current?.focus(); setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Fecha ao clicar fora
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const irPara = (e: MenuEntry) => {
    navigate(e.to)
    setQ(''); setOpen(false); inputRef.current?.blur()
  }

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive(a => Math.min(a + 1, resultados.length - 1)) }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (ev.key === 'Enter') { if (resultados[active]) irPara(resultados[active]) }
    else if (ev.key === 'Escape') { setQ(''); setOpen(false); inputRef.current?.blur() }
  }

  return (
    <div ref={boxRef} className="hidden md:flex flex-1 max-w-md ml-4 relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
      <input
        ref={inputRef}
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Buscar no menu... (Ctrl+K)"
        data-no-enter-advance
        className="w-full pl-9 pr-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-md focus:bg-white focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
      />
      {open && q && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-ink-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {resultados.length === 0 ? (
            <div className="px-4 py-3 text-sm text-ink-400">Nenhuma opção de menu encontrada</div>
          ) : (
            <ul className="max-h-80 overflow-auto py-1">
              {resultados.map((e, i) => (
                <li key={e.to + e.label}>
                  <button
                    onMouseEnter={() => setActive(i)}
                    onClick={() => irPara(e)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm ${
                      i === active ? 'bg-brand-50 text-brand-800' : 'text-ink-700 hover:bg-ink-50'
                    }`}>
                    <span className="flex-1 min-w-0">
                      {e.grupo && <span className="text-ink-400">{e.grupo} › </span>}
                      <span className="font-medium">{e.label}</span>
                    </span>
                    {i === active && <CornerDownLeft size={13} className="text-ink-400 shrink-0" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
